/**
 * Carousel Hero
 * 
 * Image slider/carousel with multiple slides.
 * Great for showcasing multiple images or messages.
 */

import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Heart, Users, Calendar, ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import { useSiteConfig } from "@/lib/useSiteConfig";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { useLayout } from "@/lib/layout-provider";
import { cn } from "@/lib/utils";
import { useQuery } from "@tanstack/react-query";
import { heroImageService } from "@/services/heroImageService";
import { queryKeys } from "@/lib/query-config";
import { RichTextDisplay } from "@/components/ui/rich-text-display";


const HeroCarousel = () => {
  const { features, data } = useSiteSettings();
  const { currentLayout } = useLayout();
  const config = useSiteConfig();
  const { hero } = config;
  const heroConfig = currentLayout.hero;
  
  const heroImageEnabled = (data?.features as Record<string, unknown>)?.heroImage !== false;

  // Fetch active hero images
  const { data: heroImages, isLoading: heroImagesLoading } = useQuery({
    queryKey: queryKeys.heroImages.active(),
    queryFn: async () => {
      const result = await heroImageService.getActiveHeroImages();
      if (!result.success) return [];
      return result.data;
    },
    enabled: heroImageEnabled,
    staleTime: 0, // Always fetch fresh data to show latest hero images
    refetchOnMount: true,
    refetchOnWindowFocus: true,
  });

  // Convert hero images to slides format
  const slides = !heroImagesLoading && heroImages && heroImages.length > 0
    ? heroImages.map((heroImg) => ({
        id: heroImg.id,
        title: heroImg.title || hero.title,
        subtitle: heroImg.subtitle || hero.subtitle,
        image: heroImg.photo?.image_url || '',
      }))
    : [];
  
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  const heightClasses = {
    full: 'min-h-screen',
    large: 'min-h-[80vh]',
    medium: 'min-h-[60vh]',
    small: 'min-h-[40vh]',
  };

  const nextSlide = useCallback(() => {
    if (slides.length === 0) return;
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  }, [slides.length]);

  const prevSlide = useCallback(() => {
    if (slides.length === 0) return;
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  }, [slides.length]);

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
    setIsAutoPlaying(false);
    // Resume auto-play after 10 seconds
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  // Auto-play
  useEffect(() => {
    if (!isAutoPlaying || slides.length === 0) return;
    
    const interval = setInterval(nextSlide, 5000);
    return () => clearInterval(interval);
  }, [isAutoPlaying, nextSlide, slides.length]);

  useEffect(() => {
    if (slides.length > 0 && currentSlide >= slides.length) {
      setCurrentSlide(0);
    }
  }, [slides.length, currentSlide]);

  return (
    <section className={cn(
      "relative flex items-center justify-center overflow-hidden",
      heightClasses[heroConfig.height]
    )}>
      {/* Slides */}
      {slides.length === 0 ? (
        <div className="absolute inset-0 bg-primary" />
      ) : (
        slides.map((slide, index) => (
          <div
            key={slide.id}
            className={cn(
              "absolute inset-0 transition-opacity duration-700",
              index === currentSlide ? "opacity-100" : "opacity-0"
            )}
          >
            {slide.image && (
              <img
                src={slide.image}
                alt={slide.title}
                className="w-full h-full object-cover"
              />
            )}
            <div 
              className="absolute inset-0 bg-gradient-to-r from-primary/80 via-primary/60 to-transparent"
              style={{ opacity: heroConfig.overlayOpacity / 100 }}
            />
          </div>
        ))
      )}

      {/* Navigation Arrows */}
      <button
        onClick={prevSlide}
        className="absolute left-4 top-1/2 -translate-y-1/2 z-20 p-3 bg-white/20 hover:bg-white/30 rounded-full backdrop-blur-sm transition-colors"
        aria-label="Previous slide"
      >
        <ChevronLeft className="w-6 h-6 text-white" />
      </button>
      <button
        onClick={nextSlide}
        className="absolute right-4 top-1/2 -translate-y-1/2 z-20 p-3 bg-white/20 hover:bg-white/30 rounded-full backdrop-blur-sm transition-colors"
        aria-label="Next slide"
      >
        <ChevronRight className="w-6 h-6 text-white" />
      </button>

      {/* Slide Indicators */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex gap-2">
        {slides.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className={cn(
              "w-3 h-3 rounded-full transition-all",
              index === currentSlide 
                ? "bg-white w-8" 
                : "bg-white/50 hover:bg-white/70"
            )}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <div className="max-w-3xl mx-auto">
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
            {slides[currentSlide].title}
            <span className="block bg-gradient-to-r from-accent to-heritage bg-clip-text text-transparent mt-2">
              {hero.highlightedText}
            </span>
          </h1>
          
          <div className="text-xl md:text-2xl text-white/90 mb-8 leading-relaxed">
            <RichTextDisplay content={slides[currentSlide].subtitle || ''} prose={false} />
          </div>

          {/* CTA Buttons */}
          {heroConfig.showCTA && (
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              {features.membership.enabled && (
                <Button variant="heritage" size="lg" asChild className="group">
                  <Link to="/membership">
                    <Users className="w-5 h-5" />
                    Join Us
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </Button>
              )}
              
              {features.donation.enabled && (
                <Button variant="donate" size="lg" asChild className="group">
                  <Link to="/donate">
                    <Heart className="w-5 h-5" />
                    Support
                  </Link>
                </Button>
              )}
              
              {features.events.enabled && (
                <Button variant="culture" size="lg" asChild className="group">
                  <Link to="/events">
                    <Calendar className="w-5 h-5" />
                    Events
                  </Link>
                </Button>
              )}

              {!features.membership.enabled && !features.donation.enabled && !features.events.enabled && (
                <Button variant="heritage" size="lg" asChild className="group">
                  <Link to="/about-us">
                    Learn More
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </Button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Bottom Gradient */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-background to-transparent" />
    </section>
  );
};

export default HeroCarousel;

